import Service from '@ember/service';

import {
  MOCK_LRAP_PROGRAMS,
  getFinancialSnapshotForClient,
  getLrapDiscoveryForClient,
  getPslfJourneyForClient,
  getPslfVaultForClient,
  getSection127RecommendationForClient,
  getStackingOptimizationForClient,
} from 'tio-employee/utils/career-pathways/mock-data';

import type {
  FinancialSnapshot,
  LrapDiscoverySummary,
  LrapEligibility,
  LrapEnrollment,
  LrapProgram,
  LrapProgramType,
  PslfAchievement,
  PslfAutomationStatus,
  PslfJourney,
  PslfPaymentValidation,
  PslfProgress,
  PslfProjection,
  PslfProtectionStatus,
  PslfScenario,
  PslfVaultDocument,
  PslfVaultSummary,
  Section127Allocation,
  Section127Recommendation,
  StackingOptimization,
} from 'tio-employee/utils/career-pathways/types';

// Re-export all types for backwards compatibility
export type {
  EmployerContributions,
  FinancialSnapshot,
  LrapDiscoverySummary,
  LrapEligibility,
  LrapEligibilityStatus,
  LrapEnrollment,
  LrapProgram,
  LrapProgramType,
  OptimizationFactor,
  OptimizationPotential,
  OutsideFundsConflict,
  ProjectedImpact,
  PslfAchievement,
  PslfAutomationStatus,
  PslfJourney,
  PslfMilestone,
  PslfPaymentValidation,
  PslfPolicyUpdate,
  PslfProgress,
  PslfProjection,
  PslfProtectionStatus,
  PslfQualificationCheck,
  PslfRiskFactor,
  PslfScenario,
  PslfVaultDocument,
  PslfVaultSummary,
  RecommendedAction,
  Section127Allocation,
  Section127Recommendation,
  StackingAllocation,
  StackingOptimization,
  StudentLoanSummary,
} from 'tio-employee/utils/career-pathways/types';

/**
 * Career Pathways API Service
 *
 * This service provides mock API responses for career pathways data.
 * In production, these will be replaced with actual API calls to tio-api.
 *
 * Types are defined in 'tio-employee/utils/career-pathways/types'
 * Mock data helpers are in 'tio-employee/utils/career-pathways/mock-data'
 */
export default class CareerPathwaysApiService extends Service {
  // Simulate network latency (300-600ms)
  private simulateDelay(): Promise<void> {
    const delay = 300 + Math.random() * 300;
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  async fetchFinancialSnapshot(): Promise<FinancialSnapshot> {
    await this.simulateDelay();
    return getFinancialSnapshotForClient();
  }

  async fetchSection127Recommendation(): Promise<Section127Recommendation> {
    await this.simulateDelay();
    return getSection127RecommendationForClient();
  }

  async updateSection127Allocation(allocation: {
    loanAllocation: number;
    tuitionAllocation: number;
    strategy: Section127Allocation['strategy'];
  }): Promise<Section127Allocation> {
    await this.simulateDelay();
    // In production, this would PATCH to the API
    const snapshot = getFinancialSnapshotForClient();
    return {
      ...snapshot.section127,
      loanAllocation: allocation.loanAllocation,
      tuitionAllocation: allocation.tuitionAllocation,
      strategy: allocation.strategy,
      updatedAt: new Date().toISOString(),
    };
  }

  async fetchLrapDiscovery(): Promise<LrapDiscoverySummary> {
    await this.simulateDelay();
    return getLrapDiscoveryForClient();
  }

  /**
   * Synchronous fetch for LRAP discovery - demo purposes only.
   * In production, this would be loaded via route model and passed as args.
   */
  fetchLrapDiscoverySyncForDemo(): LrapDiscoverySummary {
    return getLrapDiscoveryForClient();
  }

  async fetchLrapPrograms(filters?: {
    type?: LrapProgramType;
    state?: string;
  }): Promise<LrapProgram[]> {
    await this.simulateDelay();
    let programs = [...MOCK_LRAP_PROGRAMS];
    if (filters?.type) {
      programs = programs.filter((p) => p.type === filters.type);
    }
    return programs;
  }

  async checkLrapEligibility(programId: string): Promise<LrapEligibility | null> {
    await this.simulateDelay();
    const discovery = getLrapDiscoveryForClient();
    return discovery.eligiblePrograms.find((e) => e.programId === programId) || null;
  }

  async enrollInLrap(programId: string): Promise<LrapEnrollment> {
    await this.simulateDelay();
    const program = MOCK_LRAP_PROGRAMS.find((p) => p.id === programId);
    return {
      id: `enrollment-${Date.now()}`,
      programId,
      programName: program?.name || 'Unknown Program',
      programType: program?.type || 'employer',
      enrolledDate: new Date().toISOString(),
      annualBenefit: program?.maxAnnual || 0,
      ytdReceived: 0,
      lifetimeReceived: 0,
      nextPaymentDate: null,
      status: 'pending',
      expirationDate: null,
    };
  }

  async updateLrapDiscoveryStatus(completed: boolean): Promise<void> {
    await this.simulateDelay();
    // In production, this would PATCH to the API
    console.log('LRAP Discovery status updated:', completed);
  }

  async fetchStackingOptimization(): Promise<StackingOptimization> {
    await this.simulateDelay();
    return getStackingOptimizationForClient();
  }

  async fetchPslfVault(): Promise<PslfVaultSummary> {
    await this.simulateDelay();
    return getPslfVaultForClient();
  }

  async fetchPslfProgress(): Promise<PslfProgress> {
    await this.simulateDelay();
    const vault = getPslfVaultForClient();
    return vault.progress;
  }

  async uploadPslfDocument(document: {
    formType: PslfVaultDocument['formType'];
    file: File;
    employerName: string;
    coversPeriod: { start: string; end: string };
  }): Promise<PslfVaultDocument> {
    await this.simulateDelay();
    // In production, this would POST to the API with file upload
    return {
      id: `doc-${Date.now()}`,
      formType: document.formType,
      filename: document.file.name,
      uploadedAt: new Date().toISOString(),
      certifiedDate: null,
      employerName: document.employerName,
      coversPeriod: document.coversPeriod,
      paymentsCertified: 0,
      status: 'submitted',
      rejectionReason: null,
      downloadUrl: '#',
    };
  }

  async generatePslfForm(
    _formType: 'ecf'
  ): Promise<{ downloadUrl: string; prefilledData: object }> {
    await this.simulateDelay();
    // In production, this would generate a pre-filled ECF form
    return {
      downloadUrl: '#generated-form',
      prefilledData: {
        employerName: 'Current Employer',
        employerEIN: '12-3456789',
        employmentStartDate: '2022-01-15',
      },
    };
  }

  async fetchPslfJourney(): Promise<PslfJourney> {
    await this.simulateDelay();
    return getPslfJourneyForClient();
  }

  async fetchPslfProjection(): Promise<PslfProjection> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.projection;
  }

  async fetchPslfPayments(): Promise<{
    payments: PslfPaymentValidation[];
    summary: { qualifying: number; pending: number; nonQualifying: number };
  }> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return {
      payments: journey.recentPayments,
      summary: journey.allPaymentsCount,
    };
  }

  async fetchPslfPaymentDetail(paymentId: string): Promise<PslfPaymentValidation | null> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.recentPayments.find((p) => p.id === paymentId) ?? null;
  }

  async fetchPslfAutomationStatus(): Promise<PslfAutomationStatus> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.automationStatus;
  }

  async fetchPslfProtectionStatus(): Promise<PslfProtectionStatus> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.protectionStatus;
  }

  async fetchPslfAchievements(): Promise<{
    achievements: PslfAchievement[];
    nextAchievement: PslfAchievement | null;
  }> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return {
      achievements: journey.achievements,
      nextAchievement: journey.nextAchievement,
    };
  }

  async fetchPslfScenarios(): Promise<PslfScenario[]> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.scenarios;
  }

  async calculatePslfScenario(
    scenarioType: PslfScenario['type'],
    _params?: Record<string, unknown>
  ): Promise<PslfScenario> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    const scenario = journey.scenarios.find((s) => s.type === scenarioType);
    if (!scenario) {
      throw new Error(`Unknown scenario type: ${scenarioType}`);
    }
    return scenario;
  }

  async celebratePslfMilestone(milestoneId: string): Promise<PslfAchievement | null> {
    await this.simulateDelay();
    const journey = getPslfJourneyForClient();
    return journey.achievements.find((a) => a.id === milestoneId) ?? null;
  }
}
